import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { schwachstellenService } from '../../services/schwachstellenService';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../hooks/NotificationContext';
import { SchwachstelleKommentar } from '../../types';
import { VERWALTUNG_MANAGE } from '../../constants/permissions';
import { formatDistanceToNow, format } from 'date-fns';
import { de } from 'date-fns/locale';

interface KommentarSectionProps {
  schwachstelleId: number;
  refreshTrigger?: number;
}

const KommentarSection: React.FC<KommentarSectionProps> = ({ schwachstelleId, refreshTrigger }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError } = useNotification();

  const [kommentare, setKommentare] = useState<SchwachstelleKommentar[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const isAdmin = hasPermission(VERWALTUNG_MANAGE);

  const loadKommentare = useCallback(async () => {
    try {
      const data = await schwachstellenService.getKommentare(schwachstelleId);
      setKommentare(data);
    } catch {
      // handled by global interceptor
    } finally {
      setLoading(false);
    }
  }, [schwachstelleId]);

  useEffect(() => {
    loadKommentare();
  }, [loadKommentare, refreshTrigger]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    try {
      setSubmitting(true);
      await schwachstellenService.createKommentar(schwachstelleId, newComment.trim());
      setNewComment('');
      showSuccess('Kommentar hinzugefügt');
      await loadKommentare();
    } catch {
      // handled by global interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (kommentarId: number) => {
    if (!editText.trim()) return;
    try {
      await schwachstellenService.updateKommentar(schwachstelleId, kommentarId, editText.trim());
      setEditingId(null);
      setEditText('');
      showSuccess('Kommentar aktualisiert');
      await loadKommentare();
    } catch {
      // handled by global interceptor
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await schwachstellenService.deleteKommentar(schwachstelleId, deleteTarget);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      showSuccess('Kommentar gelöscht');
      await loadKommentare();
    } catch {
      // handled by global interceptor
    }
  };

  const startEdit = (k: SchwachstelleKommentar) => {
    setEditingId(k.id);
    setEditText(k.inhalt);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const openDeleteDialog = (id: number) => {
    setDeleteTarget(id);
    setDeleteDialogOpen(true);
  };

  const canModify = (k: SchwachstelleKommentar) => {
    if (k.typ !== 'kommentar') return false;
    return k.autor.id === user?.id || isAdmin;
  };

  const getTypConfig = (typ: string) => {
    switch (typ) {
      case 'review_abgelehnt':
        return {
          label: 'Abgelehnt',
          color: '#f44336' as const,
          icon: <BlockIcon sx={{ fontSize: 14 }} />,
          borderColor: '#f44336',
        };
      case 'review_genehmigt':
        return {
          label: 'Genehmigt',
          color: '#4caf50' as const,
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
          borderColor: '#4caf50',
        };
      default:
        return null;
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return formatDistanceToNow(d, { addSuffix: true, locale: de });
    } catch {
      return dateStr;
    }
  };

  const formatTooltipTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CommentIcon sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Kommentare
          </Typography>
          {kommentare.length > 0 && (
            <Chip label={kommentare.length} size="small" sx={{ ml: 0.5 }} />
          )}
        </Box>

        {kommentare.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Noch keine Kommentare vorhanden.
          </Typography>
        )}

        {kommentare.map((k, idx) => {
          const typConfig = getTypConfig(k.typ);
          const isEditing = editingId === k.id;

          return (
            <React.Fragment key={k.id}>
              {idx > 0 && <Divider sx={{ my: 1.5 }} />}
              <Box
                sx={{
                  py: 1.5,
                  px: 1.5,
                  borderRadius: 1,
                  ...(typConfig && {
                    borderLeft: `3px solid ${typConfig.borderColor}`,
                    bgcolor: alpha(typConfig.borderColor, isDark ? 0.08 : 0.04),
                  }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {k.autor.vorname} {k.autor.nachname}
                  </Typography>
                  {typConfig && (
                    <Chip
                      icon={typConfig.icon}
                      label={typConfig.label}
                      size="small"
                      sx={{
                        bgcolor: alpha(typConfig.color, isDark ? 0.2 : 0.1),
                        color: typConfig.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  )}
                  <Tooltip title={formatTooltipTime(k.erstellt_am)}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(k.erstellt_am)}
                    </Typography>
                  </Tooltip>
                  {k.bearbeitet_am && (
                    <Tooltip title={`Bearbeitet: ${formatTooltipTime(k.bearbeitet_am)}`}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        (bearbeitet)
                      </Typography>
                    </Tooltip>
                  )}
                  {canModify(k) && !isEditing && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => startEdit(k)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => openDeleteDialog(k.id)}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {isEditing ? (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      size="small"
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={() => handleEdit(k.id)}
                        disabled={!editText.trim()}
                      >
                        Speichern
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={cancelEdit}
                      >
                        Abbrechen
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {k.inhalt}
                  </Typography>
                )}
              </Box>
            </React.Fragment>
          );
        })}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            placeholder="Kommentar schreiben..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleSubmit();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
            sx={{ minWidth: 'auto', px: 2, py: 1 }}
          >
            {submitting ? <CircularProgress size={20} /> : <SendIcon />}
          </Button>
        </Box>
      </CardContent>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Kommentar löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie diesen Kommentar wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default KommentarSection;
